import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { useTranslation } from "react-i18next";

import { qk } from "@/api/query-keys";
import { notifySuccess } from "@/lib/notify";
import type { TaskRef } from "@/features/new-application/use-start-application";

import { buildApprovalPayload } from "./build-approval-payload";

export type BulkApproveResult = {
  readonly approved: number;
  /** 判定項目が無く承認できなかった案件（提出していない）。 */
  readonly skipped: readonly number[];
  /** 提出やフォーム取得で失敗した案件。 */
  readonly failed: readonly number[];
};

/**
 * 選択したタスクをまとめて承認する。
 *
 * 判定の項目名はタスクごとに違うので、フォーム定義を読んでから payload を作る
 * （名前を決め打ちにすると別のフローで壊れる）。判定項目が見つからないタスクは
 * 提出せずスキップし、勝手な値を送らない。
 *
 * 1 件失敗しても残りは続ける。途中で止めると「どこまで通ったか」が分からなくなる。
 * 直列に実行するのは、同じ案件の後続タスクが即座に開くケースで競合させないため。
 */
export async function bulkApprove(
  client: ApiClient,
  targets: readonly TaskRef[],
  comment?: string,
): Promise<BulkApproveResult> {
  let approved = 0;
  const skipped: number[] = [];
  const failed: number[] = [];

  for (const target of targets) {
    try {
      const task = await client.get<{
        readonly form_schema?: Record<string, unknown> | null;
      }>(`/tasks/${target.caseId}/${target.taskGuid}`, {
        query: { with_form_data: true },
      });

      const payload = buildApprovalPayload(task.form_schema, comment);
      if (payload === null) {
        skipped.push(target.caseId);
        continue;
      }

      await client.put(`/tasks/${target.caseId}/${target.taskGuid}`, payload);
      approved += 1;
    } catch {
      failed.push(target.caseId);
    }
  }

  return { approved, skipped, failed };
}

export function useBulkApprove(): UseMutationResult<
  BulkApproveResult,
  Error,
  readonly TaskRef[]
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");

  return useMutation({
    mutationFn: (targets: readonly TaskRef[]) => bulkApprove(client, targets),
    onSuccess: async (result) => {
      // 画面が変わらない操作なので、通ったことと件数を知らせる
      // （一部だけ通ることがある）
      notifySuccess(t("toast.bulkApproved", { count: result.approved }));

      await queryClient.invalidateQueries({ queryKey: qk.tasks._root() });
      await queryClient.invalidateQueries({
        queryKey: qk.processInstances._root(),
      });
    },
  });
}
